import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Phone, TrendingUp, MessageSquare, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { apiClient, WabaStatistics } from '../services/api';

export default function Dashboard() {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<WabaStatistics['statistics'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [wabaAccounts, setWabaAccounts] = useState<any[]>([]);

  useEffect(() => {
    loadWabaAccountsAndStats();
  }, [accessToken]);

  const loadWabaAccountsAndStats = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      // Buscar contas WABA
      const wabaResponse = await apiClient.getWabaAccounts(accessToken);

      if (wabaResponse.success && wabaResponse.data?.wabaAccounts?.length > 0) {
        setWabaAccounts(wabaResponse.data.wabaAccounts);

        // Usar a primeira conta ativa para buscar estatísticas
        const activeAccount = wabaResponse.data.wabaAccounts.find((acc: any) => acc.status === 'active');

        if (activeAccount) {
          const statsResponse = await apiClient.getWabaStatistics(
            activeAccount.id,
            accessToken
          );

          if (statsResponse.success) {
            setStatistics(statsResponse.data.statistics);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="GenIAI Dashboard" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-accent-1 rounded-full flex items-center justify-center">
              <User size={32} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-text-1">
                {user?.name}
              </h2>
              <p className="text-text-2">{user?.email}</p>
              <div className="flex gap-2 mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-1/10 text-accent-1">
                  {user?.role}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-bg-3 text-text-2">
                  ID: {user?.tenantId}
                </span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-text-2">Carregando estatísticas...</p>
          </div>
        ) : statistics ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-text-2">
                    Mensagens Enviadas
                  </h3>
                  <MessageSquare className="text-accent-1" size={20} />
                </div>
                <p className="text-3xl font-bold text-text-1">
                  {statistics.totalMessagesSent}
                </p>
                <p className="text-text-3 text-sm mt-1">Total geral</p>
              </div>

              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-text-2">
                    Taxa de Entrega
                  </h3>
                  <CheckCircle className="text-success" size={20} />
                </div>
                <p className="text-3xl font-bold text-success">
                  {statistics.deliveryRate.toFixed(1)}%
                </p>
                <p className="text-text-3 text-sm mt-1">
                  {statistics.messagesDelivered} entregues
                </p>
              </div>

              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-text-2">
                    Taxa de Leitura
                  </h3>
                  <TrendingUp className="text-accent-2" size={20} />
                </div>
                <p className="text-3xl font-bold text-accent-2">
                  {statistics.readRate.toFixed(1)}%
                </p>
                <p className="text-text-3 text-sm mt-1">
                  {statistics.messagesRead} lidas
                </p>
              </div>

              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-text-2">
                    Total de Disparos
                  </h3>
                  <Phone className="text-accent-1" size={20} />
                </div>
                <p className="text-3xl font-bold text-text-1">
                  {statistics.totalDispatches}
                </p>
                <p className="text-text-3 text-sm mt-1">
                  {statistics.successfulDispatches} bem-sucedidos
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-text-1 mb-4">
                  Conversas por Tipo
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Marketing</span>
                    <span className="text-text-1 font-semibold">
                      {statistics.marketingConversations}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Utilidade</span>
                    <span className="text-text-1 font-semibold">
                      {statistics.utilityConversations}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Autenticação</span>
                    <span className="text-text-1 font-semibold">
                      {statistics.authenticationConversations}
                    </span>
                  </div>
                  <div className="pt-3 border-t border-bg-3">
                    <div className="flex justify-between items-center">
                      <span className="text-text-1 font-medium">Total</span>
                      <span className="text-text-1 font-bold">
                        {statistics.totalConversations}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-text-1 mb-4">
                  Custos
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Custo com Templates</span>
                    <span className="text-text-1 font-semibold">
                      R$ {statistics.templateCostBRL.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Custo de Serviço</span>
                    <span className="text-text-1 font-semibold">
                      R$ {statistics.serviceCostBRL.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-2">Custo Médio/Conversa</span>
                    <span className="text-text-1 font-semibold">
                      R$ {statistics.avgCostPerConversation.toFixed(2)}
                    </span>
                  </div>
                  <div className="pt-3 border-t border-bg-3">
                    <div className="flex justify-between items-center">
                      <span className="text-text-1 font-medium">Total</span>
                      <span className="text-text-1 font-bold text-lg">
                        R$ {statistics.totalCostBRL.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-text-1 mb-4">
                Informações da Conta WhatsApp
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-text-3 text-sm">Qualidade da Conta</p>
                  <p className="text-text-1 font-semibold mt-1">
                    {statistics.qualityRating}
                  </p>
                </div>
                <div>
                  <p className="text-text-3 text-sm">Limite de Mensagens</p>
                  <p className="text-text-1 font-semibold mt-1">
                    {statistics.messagingLimitTier}
                  </p>
                </div>
                <div>
                  <p className="text-text-3 text-sm">Destinatários Alcançados</p>
                  <p className="text-text-1 font-semibold mt-1">
                    {statistics.totalRecipientsReached}
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="mt-6 bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-text-1 mb-4">
              Bem-vindo ao GenIAI!
            </h3>
            <p className="text-text-2 mb-4">
              Você está autenticado e pronto para começar a usar a plataforma.
              Configure uma conta WhatsApp Business para começar a ver suas estatísticas.
            </p>
            <button
              onClick={() => navigate('/waba')}
              className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 focus:ring-offset-bg-2"
            >
              <Phone size={18} />
              Gerenciar Contas WhatsApp
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
